import React, { useState } from 'react';
import { FileText, MessageSquare, AlertCircle, FolderOpen, ChevronDown, ChevronRight } from 'lucide-react';

const SentimentInsightsUI = ({ response }: any) => {
  const [expandedInsight, setExpandedInsight] = useState<number | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<{ insight: number; category: number } | null>(null);
  const [expandedSentiment, setExpandedSentiment] = useState<{ insight: number; category: number; sentiment: number } | null>(null);
  const insights = response || [];
  

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.85) return 'bg-green-100 text-green-800 border-green-300';
    if (confidence >= 0.7) return '  bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.85) return 'High';
    if (confidence >= 0.7) return 'Medium';
    return 'Low';
  };

  const confidencebar = (confidence:number)=>{
    if (confidence >= 0.85) return 'bg-green-500 ';
    if (confidence >= 0.7) return '  bg-yellow-500 ';
    return 'bg-red-500 ';
  }

  return (
    <div className="h-[90vh] p-4 border rounded-xl border-gray-300 overflow-y-auto pn_scroller">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <FolderOpen className="w-3 h-3 text-blue-600" />
          <h1 className="text-md font-semibold text-slate-800">Sentiment Insights by Document</h1>
        </div>

        {/* Loop through all insight files */}
        {insights.map((insight: any, insightIndex: number) => (
          <div key={insightIndex} className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            {/* File Header */}
            <div
              className="p-2 cursor-pointer flex justify-between items-center hover:bg-slate-50 transition"
              onClick={() => setExpandedInsight(expandedInsight === insightIndex ? null : insightIndex)}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-3 h-3 text-blue-600" />
                <h2 className="text-sm font-semibold text-slate-800">{insight.name}</h2>
              </div>
              {expandedInsight === insightIndex ? (
                <ChevronDown className="w-2 h-2 text-slate-500" />
              ) : (
                <ChevronRight className="w-2 h-2 text-slate-500" />
              )}
            </div>

            {/* Expanded Insight */}
            {expandedInsight === insightIndex && (
              <div className="p-4 border-t border-slate-200 bg-slate-50 space-y-3">
                <div className="text-xs text-slate-500 font-medium mb-2">
                  Total Categories: {insight.sentiment_insights?.total_categories}
                </div>

                {/* Loop over sentiment categories */}
                {insight.sentiment_insights?.results.map((category: any, catIndex: number) => (
                  <div key={catIndex} className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                    {/* Category Header */}
                    <div
                      className="p-3 flex justify-between items-center cursor-pointer hover:bg-blue-50"
                      onClick={() =>
                        setExpandedCategory(
                          expandedCategory?.insight === insightIndex && expandedCategory?.category === catIndex
                            ? null
                            : { insight: insightIndex, category: catIndex }
                        )
                      }
                    >
                      <div className="flex items-center gap-2">
                        <MessageSquare
                          className={`w-3 h-3 ${
                            category.category === 'negative'
                              ? 'text-red-500'
                              : category.category === 'positive'
                              ? 'text-green-500'
                              : 'text-slate-500'
                          }`}
                        />
                        <h3 className="text-sm font-semibold capitalize text-slate-800">
                          {category.category} ({category.sentiments.length})
                        </h3>
                      </div>
                      {expandedCategory?.insight === insightIndex && expandedCategory?.category === catIndex ? (
                        <ChevronDown className="w-2 h-2 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-2 h-2 text-slate-500" />
                      )}
                    </div>

                    {/* Sentiments under category */}
                    {expandedCategory?.insight === insightIndex && expandedCategory?.category === catIndex && (
                      <div className="p-3 space-y-2 bg-slate-50">
                        {category.sentiments.map((item: any, sentIndex: number) => (
                          <div
                            key={sentIndex}
                            className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
                          >
                            <div
                              className="p-3 cursor-pointer"
                              onClick={() =>
                                setExpandedSentiment(
                                  expandedSentiment?.insight === insightIndex &&
                                  expandedSentiment?.category === catIndex &&
                                  expandedSentiment?.sentiment === sentIndex
                                    ? null
                                    : { insight: insightIndex, category: catIndex, sentiment: sentIndex }
                                )
                              }
                            >
                              <div className="flex justify-between items-start gap-3">
                                <div className="flex items-center gap-2">
                                  <AlertCircle className="w-3 h-3 text-blue-600 mt-0.5" />
                                  <h4 className="text-xs font-semibold text-slate-800">
                                    {item.sentiment}
                                  </h4>
                                </div>
                                <div className="flex flex-col items-end">
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getConfidenceColor(
                                      item.confidence
                                    )}`}
                                  >
                                    {getConfidenceLabel(item.confidence)} Confidence
                                  </span>
                                  <span className="text-[10px] text-slate-500 font-mono">
                                    {(item.confidence * 100).toFixed(0)}%
                                  </span>
                                </div>
                              </div>

                              {/* Confidence Bar */}
                              <div className="mt-2 w-full bg-slate-200 rounded-full h-1">
                                <div
                                  className={`${confidencebar(
                                      item.confidence
                                    )} h-1 rounded-full transition-all`}
                                  style={{ width: `${item.confidence * 100}%` }}
                                ></div>
                              </div>
                            </div>

                            {/* Source Quote */}
                            {expandedSentiment?.insight === insightIndex &&
                              expandedSentiment?.category === catIndex &&
                              expandedSentiment?.sentiment === sentIndex && (
                                <div className="px-4 pb-3 pt-2 bg-slate-50 border-t border-slate-200">
                                  <p className="text-[11px] text-slate-500 font-medium uppercase mb-1">Source Quote</p>
                                  <div className="bg-white rounded-lg p-3 border-l-4 border-blue-400">
                                    <p className="text-xs text-slate-700 italic">"{item.source}"</p>
                                  </div>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <div className="mt-4 bg-blue-50 rounded-lg p-3 border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> Click on a document → then a category → then a sentiment to view its quote.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SentimentInsightsUI;
